import { useState, useEffect, useContext, createContext } from 'react';
import { supabase } from '@shared/supabaseClient';

/**
 * Feature Access Hook - Controls access to tier-gated features
 * 
 * Tiers: basic, pro, enterprise
 * Features are stored in app_settings.subscription
 */

// Default subscription (basic tier)
const DEFAULT_SUBSCRIPTION = {
  tier: 'basic',
  features: {
    competency_signoff: false,
    custom_signoff_rules: false,
    hours_tracking: false,
    missed_tests: false,
    multi_site: false,
    advanced_reporting: false,
    scheduled_reports: false,
    api_access: false,
    sms_notifications: false,
    white_label: false,
    custom_fields: false,
    sso: false,
    executive_kpis: false,
    depreciation: false,
    sla_tracking: false,
    compliance_mode: false,
    offline_sync: false,  // Pro/Enterprise: Queue operations offline, auto-sync when back online
    messaging: false,          // Pro/Enterprise: In-app messaging system
    push_notifications: false, // Enterprise: Push notifications for messages
  },
  limits: {
    max_users: 10,
    max_equipment: 50,
    max_locations: 5,
    storage_gb: 1,
    data_retention_years: 1,
  },
};

// Feature keys for easy reference
export const FEATURES = {
  COMPETENCY_SIGNOFF: 'competency_signoff',
  CUSTOM_SIGNOFF_RULES: 'custom_signoff_rules',
  HOURS_TRACKING: 'hours_tracking',
  MISSED_TESTS: 'missed_tests',
  MULTI_SITE: 'multi_site',
  ADVANCED_REPORTING: 'advanced_reporting',
  SCHEDULED_REPORTS: 'scheduled_reports',
  API_ACCESS: 'api_access',
  SMS_NOTIFICATIONS: 'sms_notifications',
  WHITE_LABEL: 'white_label',
  CUSTOM_FIELDS: 'custom_fields',
  SSO: 'sso',
  EXECUTIVE_KPIS: 'executive_kpis',
  DEPRECIATION: 'depreciation',
  SLA_TRACKING: 'sla_tracking',
  COMPLIANCE_MODE: 'compliance_mode',
  OFFLINE_SYNC: 'offline_sync',
  MESSAGING: 'messaging',
  PUSH_NOTIFICATIONS: 'push_notifications',
};

// Limit keys
export const LIMITS = {
  MAX_USERS: 'max_users',
  MAX_EQUIPMENT: 'max_equipment',
  MAX_LOCATIONS: 'max_locations',
  STORAGE_GB: 'storage_gb',
  DATA_RETENTION_YEARS: 'data_retention_years',
};

// Tier names
export const TIERS = {
  BASIC: 'basic',
  PRO: 'pro',
  ENTERPRISE: 'enterprise',
};

// Create context for subscription data
const SubscriptionContext = createContext(null);

/**
 * Subscription Provider - Wrap app to provide subscription context
 */
export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(DEFAULT_SUBSCRIPTION);
  const [tierReference, setTierReference] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      // Load both subscription and tier reference
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', ['subscription', 'tier_reference']);

      if (error) {
        console.error('Failed to load subscription:', error);
        return;
      }

      if (data) {
        const subData = data.find(d => d.key === 'subscription');
        const refData = data.find(d => d.key === 'tier_reference');
        
        if (subData?.value) {
          setSubscription({ ...DEFAULT_SUBSCRIPTION, ...subData.value });
        }
        if (refData?.value) {
          setTierReference(refData.value);
        }
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSubscription = async (newSubscription) => {
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({ value: newSubscription })
        .eq('key', 'subscription');

      if (error) throw error;

      setSubscription(newSubscription);
      return { success: true };
    } catch (error) {
      console.error('Failed to update subscription:', error);
      return { success: false, error };
    }
  };

  const value = {
    subscription,
    tierReference,
    isLoading,
    updateSubscription,
    refreshSubscription: loadSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 */
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    // Return default if not wrapped in provider (graceful fallback)
    return {
      subscription: DEFAULT_SUBSCRIPTION,
      tierReference: null,
      isLoading: false,
      updateSubscription: async () => ({ success: false }),
      refreshSubscription: () => {},
    };
  }
  return context;
}

/**
 * Hook to check feature access
 */
export function useFeatureAccess() {
  const { subscription, isLoading } = useSubscription();

  /**
   * Check if a feature is enabled
   * @param {string} featureKey - Key from FEATURES constant
   * @returns {boolean}
   */
  const hasFeature = (featureKey) => {
    if (isLoading) return false;
    return subscription.features?.[featureKey] ?? false;
  };

  /**
   * Get a limit value
   * @param {string} limitKey - Key from LIMITS constant
   * @returns {number} - -1 means unlimited
   */
  const getLimit = (limitKey) => {
    if (isLoading) return 0;
    return subscription.limits?.[limitKey] ?? 0;
  };

  /**
   * Check if at or over a limit
   * @param {string} limitKey - Key from LIMITS constant
   * @param {number} currentCount - Current usage count
   * @returns {boolean}
   */
  const isAtLimit = (limitKey, currentCount) => {
    const limit = getLimit(limitKey);
    if (limit === -1) return false; // Unlimited
    return currentCount >= limit;
  };

  /**
   * Check if tier is at least the specified level
   * @param {string} requiredTier - 'basic', 'pro', or 'enterprise'
   * @returns {boolean}
   */
  const hasTier = (requiredTier) => {
    const tierOrder = { basic: 1, pro: 2, enterprise: 3 };
    const currentTierLevel = tierOrder[subscription.tier] || 0;
    const requiredLevel = tierOrder[requiredTier] || 0;
    return currentTierLevel >= requiredLevel;
  };

  /**
   * Get current tier name
   */
  const getCurrentTier = () => subscription.tier;

  return {
    hasFeature,
    getLimit,
    isAtLimit,
    hasTier,
    getCurrentTier,
    isLoading,
    // Convenience checks for common features
    canUseCompetencySignoff: hasFeature(FEATURES.COMPETENCY_SIGNOFF),
    canUseCustomRules: hasFeature(FEATURES.CUSTOM_SIGNOFF_RULES),
    canUseHoursTracking: hasFeature(FEATURES.HOURS_TRACKING),
    canUseAdvancedReporting: hasFeature(FEATURES.ADVANCED_REPORTING),
    canUseApi: hasFeature(FEATURES.API_ACCESS),
    canUseExecutiveKPIs: hasFeature(FEATURES.EXECUTIVE_KPIS),
    canUseSLATracking: hasFeature(FEATURES.SLA_TRACKING),
    canUseComplianceMode: hasFeature(FEATURES.COMPLIANCE_MODE),
  };
}

/**
 * Check if user is platform owner
 */
export function isPlatformOwner(user) {
  return user?.is_platform_owner === true || user?.isPlatformOwner === true;
}
